import {useState, useCallback, useEffect, useRef} from 'react';
import {uuidv4} from 'polyphony/util';

/*
    useForceUpdate()

    Returns a function that forces an update
    and a value that changes every time the update
    is forced (for memos).
*/

export const useForceUpdate = () => {
    const [slug, setState] = useState(uuidv4);
    return [slug, () => setState(uuidv4())];
};

/*
    useStateWithCallback
    Like React's useState, except with a callback that's fired with
    the new value every time it changes.
*/

export const useStateWithCallback = (initial, callback, fireInitialCallback = true) => {
    let [v, _s] = useState(() => {
        let iv = typeof initial === 'function' ? initial() : initial;
        if (fireInitialCallback && callback) callback(iv);
        return iv;
    });

    const s = useCallback(nv => {
        _s(nv);
        if (callback) callback(nv);
    }, [_s, callback]);

    return [v, s];
};

/*
    usePromise()
*/

export const usePromise = (promiseFactory, memo = []) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(undefined);
    const [result, setResult] = useState(undefined);

    // Ensure we don't call any setter
    // if the component has been unmounted
    const unmounted = useRef(false);
    useEffect(() => {
        unmounted.current = false;
        return () => {
            unmounted.current = true;
        };
    }, memo);

    const load = () => {
        if (!loading) setLoading(true);
        promiseFactory().then(d => {
            if (!unmounted.current) {
                setResult(d);
                setError(null);
                setLoading(false);
            }
        }).catch(e => {
            if (!unmounted.current) {
                setResult(undefined);
                setError(e);
                setLoading(false);
            }
        });
    };

    useEffect(load, memo);

    return {loading, error, result, reload: load};
};

